import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ThirdPartyPost,
  ThirdPartyPostAttachmentType,
  ThirdPartyPostMedia,
  ThirdPartyPostMediaType
} from '../../../../models/graphql';
import { Modal } from '../../../../components/containers/Modal';
import classes from './ZoomModal.module.scss';
import { IconsEnum } from '../../../../constants/icons';
import classNames from 'classnames';
import { FindContentContext, ProfileFindContent } from '../FindContentProvider';
import { formatDistanceStrict } from 'date-fns';
import { LightboxModal } from '../../../Publish/MediaLibrary/LightboxModal';
import { MediaGridContext } from '../../../Publish/MediaLibrary/MediaGrid/MediaGrid.view';
import { SquareButton } from '../../../../components/actions/SquareButton';
import { SquareButtonIconsEnum } from '../../../../constants/square-button-icons';
import videoPlayIcon from 'assets/images/VideoPlayButton.svg';
import { FindContentShareButton } from '../FindContentShareButton';
import { Icon } from '../../../../components/images/Icon/Icon';
import { ReactComponent as LinkPreviewNoImage } from 'assets/images/linkPreviewNoImage.svg';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import { convertLinksToAnchorTags } from '../../../../helpers/textHelpers';
import { Galleria } from 'primereact/galleria';
import {
  ThirdPartyLinkPreviewContextEnum,
  ThirdPartyLinkPreviewViewProps
} from '../ThirdPartyLinkPreview/ThirdPartyLinkPreview.view';
import { ThirdPartyLinkPreview } from '../ThirdPartyLinkPreview';
import { hasLinkAttachmentType } from '../PostCard/PostCard.view';
import { getImageDimensions, MediaDimensions } from '../../../../helpers/media';
import { ProfileMetrics } from 'constants/profileMetrics';
import { PostQueueStatsSet } from '../PostQueueStatsSet';

export type ZoomModalViewProps = {
  post: ThirdPartyPost;
  hasAnimatedGif?: boolean;
  closeHandler: (show: boolean) => void;
  availableMetrics: ProfileMetrics[];
  selectedProfile: ProfileFindContent;
};

export const ZoomModalView = ({
  post,
  hasAnimatedGif,
  closeHandler,
  availableMetrics,
  selectedProfile
}: ZoomModalViewProps): JSX.Element => {
  const { sourceType } = useContext(FindContentContext);
  const [showLightBox, setShowLightBox] = useState<boolean>(false);
  const [photoDimensions, setPhotoDimensions] = useState<MediaDimensions>({ width: 0, height: 0 });

  const isLinkPresent = useMemo(() => hasLinkAttachmentType(post), [post]);

  const hasMixedMedia =
    post.attachmentTypes.includes(ThirdPartyPostAttachmentType.VIDEO) &&
    post.attachmentTypes.includes(ThirdPartyPostAttachmentType.PHOTO);

  const dateAsString = useMemo(
    () =>
      post.createdAt
        ? formatDistanceStrict(new Date(post.createdAt), new Date(), {
            addSuffix: true,
            roundingMethod: 'ceil'
          })
        : '',
    [post.createdAt]
  );

  // Find the first video in the post.media array.
  const video = post.media.find(m => m.type === ThirdPartyPostMediaType.VIDEO);
  // If there is no video, then get all the photos.
  const photos = post.media.filter(m => m.type === ThirdPartyPostMediaType.PHOTO);
  // If neither video nor photos exist, then use the link image URL.
  const media = useMemo(
    () =>
      video ||
      (photos && photos.length > 0
        ? photos[0]
        : post.linkImageUrl
        ? { url: post.linkImageUrl, type: ThirdPartyPostMediaType.PHOTO }
        : undefined),
    [photos, post.linkImageUrl, video]
  );

  const mediaImageUrl =
    media?.type === 'VIDEO'
      ? media.thumbnailUrl
      : media?.url && photoDimensions.width > 10 && photoDimensions.height > 10
      ? media.url
      : undefined;

  const galleriaMedia = useMemo(
    () => (video && hasMixedMedia ? [...[video], ...photos] : !video ? photos : undefined),
    [hasMixedMedia, photos, video]
  );
  const galleriaItemTemplate = useCallback((item: ThirdPartyPostMedia): JSX.Element => {
    return <img src={item.thumbnailUrl ?? item.url ?? ''} alt={''} />;
  }, []);

  const zoomModalContainerClasses = classNames('grid-x', classes.ZoomModal);
  const mediaColumnClasses = classNames('cell auto', classes.MediaColumn);
  const mediaWrapperClasses = classNames('grid-y', classes.MediaWrapper);
  const mediaContainerClasses = classNames('cell', classes.MediaContainer);
  const postTextColumnClasses = classNames('cell', classes.PostTextColumn);
  const postDetailsWrapper = classNames('grid-x align-justify', classes.PostDetailsWrapper);
  const postLinkWrapperClasses = classNames('cell auto', classes.PostLinkWrapper);
  const dateStringWrapperClasses = classNames('cell shrink', classes.DateStringWrapper);
  const postQueueContainerClasses = classNames('grid-x', classes.PostQueueStatsContainer);
  const closeButtonWrapperClasses = classNames(classes.CloseButtonWrapper);
  const postActionContainerClasses = classNames(classes.PostActionContainer);
  const zoomModalActionButtonClasses = classNames('margin-bottom-small-8', classes.ZoomModalActionButton);

  const isBadgeLinkPreview = !!(!post.linkName?.length && post.linkUrl?.length);
  const linkPreviewProps: ThirdPartyLinkPreviewViewProps = {
    context: ThirdPartyLinkPreviewContextEnum.MODAL,
    linkDescription: post.linkDescription,
    linkImageUrl: post.linkImageUrl,
    linkName: post.linkName,
    linkUrl: post.linkUrl
  };

  const renderMedia = useMemo(() => {
    if (galleriaMedia && galleriaMedia.length > 1) {
      return (
        <Galleria
          className={'zoom-modal-galeria'}
          value={galleriaMedia}
          numVisible={1}
          item={galleriaItemTemplate}
          showIndicators={true}
          showItemNavigators={true}
          showThumbnails={false}
          circular={true}
        />
      );
    }

    if (media && media.type === ThirdPartyPostMediaType.VIDEO) {
      return (
        <div>
          {!media.url ? (
            <div className={classes.BrokenVideoUrlMessage}>
              <div className={classes.BoxMessageWrapper}>
                <BoxNotification type={'error'}>
                  There seems to be a problem with video playback. We are unable to play this video at the moment.
                </BoxNotification>
              </div>
            </div>
          ) : (
            <div className={classes.VideoPlayButton}>
              <img src={videoPlayIcon} alt={'Video Play Button'} onClick={() => setShowLightBox(true)} />
            </div>
          )}
          {mediaImageUrl && media.thumbnailUrl ? (
            <img src={mediaImageUrl} alt={'Video Thumbnail'} onClick={() => setShowLightBox(!!media.url)} />
          ) : (
            <div className={classes.NoImageContainer} />
          )}
        </div>
      );
    }

    if (mediaImageUrl) {
      return (
        <>
          {hasAnimatedGif && (
            <div className={classes.GifBadgeWrapper}>
              <Icon name={IconsEnum.gifBadge} size={'large'} />
            </div>
          )}
          <img src={mediaImageUrl} alt={'Post Media'} className={classes.MediaImage} />
        </>
      );
    }

    if (isLinkPresent && post.linkName) {
      return (
        <div className={classes.NoImageContainer}>
          <LinkPreviewNoImage />
        </div>
      );
    }

    return null;
  }, [galleriaItemTemplate, galleriaMedia, hasAnimatedGif, isLinkPresent, media, mediaImageUrl, post.linkName]);

  const postText = useMemo(() => {
    return convertLinksToAnchorTags(post.text);
  }, [post.text]);

  // Set image dimensions to that of the media.url we defined
  useEffect(() => {
    getImageDimensions(media?.url || '').then(dimensions => {
      setPhotoDimensions(dimensions);
    });
  }, [media]);

  const postTextContainerClasses = classNames('cell shrink', classes.PostTextContainer, {
    [classes.PostTextContainerHasBadge]: isBadgeLinkPreview && !renderMedia
  });

  const linkPreviewWithMediaWrapperClasses = classNames({
    [classes.LinkPreviewBadgeWrapper]: isBadgeLinkPreview
  });

  return (
    <Modal
      size={'zoom'}
      isBorderLess={true}
      closeOnEsc={true}
      closeOnClickOutside={true}
      onClose={() => closeHandler(false)}
      showCloseIcon={false}>
      <div className={zoomModalContainerClasses}>
        {renderMedia && (
          <div className={mediaColumnClasses}>
            <div className={mediaWrapperClasses}>
              <div className={mediaContainerClasses}>{renderMedia}</div>
              {isLinkPresent && (
                <div className={linkPreviewWithMediaWrapperClasses}>
                  <ThirdPartyLinkPreview hasTooltipBadge={true} {...linkPreviewProps} />
                </div>
              )}
            </div>
          </div>
        )}
        <div className={postTextColumnClasses}>
          <div className={'grid-y align-justify full-height'}>
            <div className={'cell auto'}>
              <div className={closeButtonWrapperClasses}>
                <SquareButton
                  type={'transparent'}
                  icon={SquareButtonIconsEnum.close}
                  action={() => closeHandler(false)}
                />
              </div>
              <div className={postTextContainerClasses}>
                <div className={classes.PostText}>{postText}</div>
              </div>
              {isLinkPresent && !renderMedia && (
                <div className={classes.LinkPreviewContainer}>
                  <div className={classes.LinkPreviewWrapper}>
                    <ThirdPartyLinkPreview hasTooltipBadge={true} {...linkPreviewProps} />
                  </div>
                </div>
              )}
            </div>
            <div className={'cell shrink'}>
              <div className={postActionContainerClasses}>
                <div className={'margin-top-small-24 margin-bottom-small-16'}>
                  <div className={postDetailsWrapper}>
                    <div className={postLinkWrapperClasses}>
                      {sourceType && (
                        <a href={String(post.externalProfile.profileUrl)} target={'_blank'} rel={'noreferrer'}>
                          @{post.externalProfile.displayName}
                        </a>
                      )}
                    </div>
                    <div className={dateStringWrapperClasses}>{dateAsString}</div>
                  </div>
                </div>

                <div className={postQueueContainerClasses}>
                  <PostQueueStatsSet {...{ availableMetrics, post, selectedProfile }} />
                </div>
              </div>
              <div className={zoomModalActionButtonClasses}>
                <FindContentShareButton post={post} isLinkPresent={isLinkPresent} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showLightBox && media && (
        <LightboxModal onClose={() => setShowLightBox(false)} mediaItem={media} context={MediaGridContext.MODAL} />
      )}
    </Modal>
  );
};
